/**
 * Breadcrumbs Component
 *
 * SEO-optimized breadcrumb navigation with structured data (JSON-LD)
 * Helps search engines understand site hierarchy and can appear in search results
 *
 * Features:
 * - WCAG AAA compliant with proper ARIA labels
 * - Structured data (BreadcrumbList schema) for SEO
 * - Responsive design
 * - Accessible keyboard navigation
 *
 * Usage:
 * - Compute breadcrumb items in your route component using useLoaderData()
 * - Pass items as props to this component
 * - This ensures server/client consistency since items come from loader data
 *
 * Example:
 * ```tsx
 * const data = useLoaderData();
 * const breadcrumbItems = [
 *   { name: 'Home', url: '/' },
 *   { name: 'Shop', url: '/shop' },
 *   { name: data.categoryName, url: `/category/${data.categorySlug}` }
 * ];
 * <Breadcrumbs items={breadcrumbItems} />
 * ```
 */

import { Link } from '@remix-run/react';

interface BreadcrumbItem {
  name: string;
  url: string;
}

interface BreadcrumbsProps {
  items: BreadcrumbItem[];
  className?: string;
  variant?: 'default' | 'light'; // 'light' for dark backgrounds
  /**
   * Emit ONLY the BreadcrumbList structured data, not the visible trail.
   *
   * For a page whose own design already draws breadcrumbs — a page-builder
   * template does — where rendering these again would duplicate them on screen.
   * The SCHEMA must still ship: dropping it silently removes a rich-result
   * signal from a page that already ranks, which is what happened the first
   * time the blog takeover rendered a converted post.
   */
  schemaOnly?: boolean;

}

export default function Breadcrumbs({
  items,
  className = '',
  variant = 'default',
  schemaOnly = false,
}: BreadcrumbsProps) {
  // Don't render if no items or only home page
  if (!items || items.length === 0 || (items.length === 1 && items[0].url === '/')) {
    return null;
  }

  // Generate structured data for SEO
  const fullUrls = items.map((item) => ({
    name: item.name,
    url: item.url.startsWith('http') ? item.url : `https://catalyst-cannabis.com${item.url}`,
  }));

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: fullUrls.map((crumb, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: crumb.name,
      item: crumb.url,
    })),
  };

  const schemaTag = (
    <script
      type="application/ld+json"
      suppressHydrationWarning
      dangerouslySetInnerHTML={{
        __html: JSON.stringify(breadcrumbSchema),
      }}
    />
  );

  if (schemaOnly) return schemaTag;

  return (
    <>
      {/* Structured Data: BreadcrumbList JSON-LD for SEO */}
      {schemaTag}

      {/* Breadcrumb Navigation */}
      <nav
        aria-label="Breadcrumb"
        className={`flex min-h-11 items-center py-2 ${className} ${
          variant === 'light' ? 'rounded-lg bg-white/90 px-3 py-1 shadow-sm' : ''
        }`}
      >
        <ol className="font-helvetica-neue flex min-w-0 flex-wrap items-center gap-y-1 text-[13px] leading-5">
          {items.map((item, index) => {
            const isLast = index === items.length - 1;
            const textColorClass =
              variant === 'light'
                ? isLast
                  ? 'text-gray-700'
                  : 'text-gray-800'
                : isLast
                  ? 'font-medium text-[#334155]'
                  : 'text-[#264B9A]';
            const linkHoverClass =
              variant === 'light' ? 'hover:text-blue-600' : 'hover:text-[#19366F] hover:underline';
            const separatorColorClass = variant === 'light' ? 'text-gray-400' : 'text-[#94A3B8]';
            // Responsive max-widths: shorter on mobile, longer on larger screens
            const truncateClass =
              'max-w-[200px] sm:max-w-[420px] md:max-w-[600px] lg:max-w-[700px]  truncate';

            return (
              <li key={`${item.url}-${index}`} className="flex items-center">
                {isLast ? (
                  <span
                    className={`${textColorClass} ${truncateClass}`}
                    aria-current="page"
                    aria-label={`Current page: ${item.name}`}
                    title={item.name}
                  >
                    {item.name}
                  </span>
                ) : (
                  <>
                    <Link
                      to={item.url}
                      className={`${textColorClass} ${linkHoverClass} ${truncateClass} transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 ${
                        index === 0 && item.name === 'Home'
                          ? 'inline-flex min-h-7 items-center'
                          : ''
                      }`}
                      aria-label={`Navigate to ${item.name}`}
                      title={item.name}
                    >
                      {item.name}
                    </Link>
                    <span className={`mx-2 ${separatorColorClass}`} aria-hidden="true">
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                        <path d="m4.5 2.5 3.5 3.5-3.5 3.5" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </span>
                  </>
                )}
              </li>
            );
          })}
        </ol>
      </nav>
    </>
  );
}
